import {
    CorporationJoinMode,
    IUserResponse,
    KcfHandling,
    LocationJoinMode,
} from '@kcftech/prometheus-client';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { usePermissions } from 'contexts/PermissionsContext';
import { useInfiniteUsersDataSource } from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { useEffect } from 'react';
import { SecondaryText } from 'styles/theme_styles/text';
import { getId } from 'utils/data';

export type User = Pick<
    IUserResponse,
    | 'Id'
    | 'FirstName'
    | 'LastName'
    | 'Email'
    | 'PhoneNumber'
    | 'ApplicationRoleCode'
    | 'Corporations'
    | 'Locations'
>;

export type UserDropdownProps = Readonly<{
    /**
     * The currently selected user.
     */
    value: User | undefined;
    /**
     * Callback to call when the selected user should change.
     */
    onChange: (user: User) => void;
    /**
     * Narrow down search results to a single corporation.
     */
    corporationId?: string;
    /**
     * Narrow down search results to a single location.
     */
    locationId?: string;
    /**
     * Applies classes to the trigger.
     */
    className?: string;
    /*
     * Allows optional ability to change corporation join mode.
     */
    corporationJoinOverride?: CorporationJoinMode;
    /*
     * Allows optional ability to change corporation join mode.
     */
    locationJoinOverride?: LocationJoinMode;
    /*
     * Allows ability to only select authorized users.
     */
    onlyActiveUsers?: boolean;
}>;

const renderLabel: DropdownLabelRenderer<User> = ({ value }) =>
    value ? `${value.FirstName} ${value.LastName}` : 'Select a user';

const renderItem: DropdownItemRenderer<User> = ({ item: user }) => (
    <div>
        <div>
            {user.FirstName} {user.LastName}
        </div>
        <SecondaryText>{user.Email}</SecondaryText>
    </div>
);

/**
 * Dropdown which allows selecting a user from a list of available users.
 */
export function UserDropdown({
    className,
    value,
    onChange,
    corporationId,
    locationId,
    corporationJoinOverride,
    locationJoinOverride,
    onlyActiveUsers,
}: UserDropdownProps) {
    const corporationIds = useValueAsTuple(corporationId);
    const { canViewKcfUsers } = usePermissions();

    const {
        results: users,
        isLoading: isLoadingUsers,
        filter,
        setFilter,
        hasNext,
        loadNext,
    } = useInfiniteUsersDataSource({
        name: 'user-dropdown',
        defaultFilter: {
            nameSearch: '',
            corporationIds,
            locationIds: locationId ? [locationId] : undefined,
            corporationJoinMode: corporationJoinOverride,
            locationJoinMode: locationJoinOverride,
            isApproved: onlyActiveUsers,
            kcfHandling: !canViewKcfUsers ? KcfHandling.Hide : KcfHandling.Include,
        },
    });

    const onFilterChange = (nameSearch: string) =>
        setFilter((current) => ({ ...current, nameSearch }));

    useEffect(
        () =>
            setFilter((current) => ({
                ...current,
                corporationIds: locationId ? undefined : corporationIds,
                locationIds: locationId ? [locationId] : undefined,
            })),
        [corporationIds, locationId, setFilter],
    );

    return (
        <Dropdown<User>
            data={{
                items: users,
                isLoading: isLoadingUsers,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            renderLabel={renderLabel}
            renderItem={renderItem}
            value={value}
            onChange={onChange}
            filterable={{
                filterText: filter.nameSearch ?? '',
                onChange: onFilterChange,
                placeholder: 'Search users',
            }}
            trigger={{ className }}
            popup={{
                width: '40rem',
            }}
        />
    );
}
